import React, { useState } from 'react';
import { Loader2, MapPin, LocateFixed, UtensilsCrossed, Building2, Plus, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export const CUISINES = [
  ['general', 'General', 'General'],
  ['mexican', 'Mexicana', 'Mexican'],
  ['italian', 'Italiana', 'Italian'],
  ['asian', 'Asiática', 'Asian'],
  ['american', 'Americana', 'American'],
  ['mediterranean', 'Mediterránea', 'Mediterranean'],
  ['seafood', 'Mariscos', 'Seafood'],
  ['steakhouse', 'Steakhouse', 'Steakhouse'],
  ['cafe_bakery', 'Café / Panadería', 'Café / Bakery'],
  ['vegan', 'Vegana', 'Vegan'],
  ['fast_casual', 'Fast casual', 'Fast casual'],
  ['fine_dining', 'Alta cocina', 'Fine dining'],
];

export const ClientOnboarding = ({ isEs, initial = {}, onSaved }) => {
  const [form, setForm] = useState({
    restaurant_name: initial.restaurant_name || '',
    cuisine: initial.cuisine || 'general',
    city: initial.city || '',
    lat: initial.lat ?? null,
    lon: initial.lon ?? null,
    is_group: initial.is_group || false,
    locations: initial.locations || [],
  });
  const [busy, setBusy] = useState(false);
  const [locating, setLocating] = useState(false);
  const [err, setErr] = useState('');

  const t = isEs ? {
    title: 'Configura tu panel', sub: 'Cuéntanos de tu restaurante para personalizar noticias, clima y oportunidades de tu zona.',
    name: 'Nombre de tu restaurante', cuisine: 'Tipo de cocina', city: 'Ciudad / zona',
    detect: 'Detectar mi ubicación', detecting: 'Detectando…', save: 'Crear mi panel', saving: 'Guardando…',
    cityPh: 'Ej: San José, CA', err: 'No pudimos guardar. Inténtalo de nuevo.', geoErr: 'No pudimos detectar tu ubicación — escribe tu ciudad.',
    group: '¿Tienes más de un restaurante?', groupSub: 'Añade tus otros locales o marcas — personalizaremos el panel para tu grupo.',
    addLoc: 'Añadir otro local', locName: 'Nombre del local', locCity: 'Ciudad',
  } : {
    title: 'Set up your dashboard', sub: 'Tell us about your restaurant so we can personalize news, weather and opportunities in your area.',
    name: 'Your restaurant name', cuisine: 'Cuisine type', city: 'City / area',
    detect: 'Detect my location', detecting: 'Detecting…', save: 'Create my dashboard', saving: 'Saving…',
    cityPh: 'E.g. San Jose, CA', err: "We couldn't save. Please try again.", geoErr: "We couldn't detect your location — type your city.",
    group: 'Do you own more than one restaurant?', groupSub: "Add your other locations or brands — we'll personalize the dashboard for your group.",
    addLoc: 'Add another location', locName: 'Location name', locCity: 'City',
  };

  const detect = () => {
    if (!navigator.geolocation) { setErr(t.geoErr); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      const { latitude, longitude } = pos.coords;
      try {
        const r = await fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=${isEs ? 'es' : 'en'}`);
        const j = await r.json();
        const city = j.city || j.locality || j.principalSubdivision || '';
        setForm((f) => ({ ...f, city, lat: latitude, lon: longitude }));
      } catch { setErr(t.geoErr); }
      setLocating(false);
    }, () => { setErr(t.geoErr); setLocating(false); }, { timeout: 10000 });
  };

  const save = async (e) => {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/client/profile`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, locale: isEs ? 'es' : 'en' }),
      });
      if (!r.ok) throw new Error();
      const d = await r.json();
      onSaved(d.user);
    } catch { setErr(t.err); }
    setBusy(false);
  };

  return (
    <div className="w-full max-w-lg rounded-3xl border border-neutral-200 bg-white p-8 shadow-[0_30px_80px_-40px_rgba(0,0,0,0.25)]" data-testid="client-onboarding">
      <div className="flex items-center gap-2.5">
        <img src="/kitxens-logo.png" alt="Kitxens" className="w-9 h-9 rounded-lg" />
        <p className="text-[#F77F11] text-[10px] tracking-[0.24em] uppercase font-bold">Mi Kitxens</p>
      </div>
      <h1 className="mt-4 text-2xl font-bold text-neutral-900">{t.title}</h1>
      <p className="mt-1.5 text-[14px] text-neutral-500 leading-relaxed">{t.sub}</p>
      <form onSubmit={save} className="mt-6 space-y-4">
        <div>
          <label className="block text-[11px] uppercase tracking-wider font-bold text-neutral-500 mb-1.5">{t.name}</label>
          <div className="relative">
            <UtensilsCrossed className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <input required value={form.restaurant_name} onChange={(e) => setForm({ ...form, restaurant_name: e.target.value })}
              className="w-full h-11 rounded-xl border border-neutral-300 pl-10 pr-4 text-[14px] outline-none focus:border-[#F77F11]" data-testid="onboarding-restaurant-input" />
          </div>
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wider font-bold text-neutral-500 mb-1.5">{t.cuisine}</label>
          <select value={form.cuisine} onChange={(e) => setForm({ ...form, cuisine: e.target.value })}
            className="w-full h-11 rounded-xl border border-neutral-300 px-3.5 text-[14px] outline-none focus:border-[#F77F11] bg-white" data-testid="onboarding-cuisine-select">
            {CUISINES.map(([v, es, en]) => <option key={v} value={v}>{isEs ? es : en}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wider font-bold text-neutral-500 mb-1.5">{t.city}</label>
          <div className="flex gap-2">
            <div className="relative flex-1 min-w-0">
              <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <input required value={form.city} placeholder={t.cityPh}
                onChange={(e) => setForm({ ...form, city: e.target.value, lat: null, lon: null })}
                className="w-full h-11 rounded-xl border border-neutral-300 pl-10 pr-4 text-[14px] outline-none focus:border-[#F77F11]" data-testid="onboarding-city-input" />
            </div>
            <button type="button" onClick={detect} disabled={locating}
              className="shrink-0 inline-flex items-center gap-1.5 h-11 px-3.5 rounded-xl border border-neutral-300 hover:border-[#F77F11] text-[12.5px] font-semibold text-neutral-600 hover:text-[#F77F11] transition-colors disabled:opacity-60"
              data-testid="onboarding-detect-btn">
              {locating ? <Loader2 className="w-4 h-4 animate-spin" /> : <LocateFixed className="w-4 h-4" />}
              <span className="hidden sm:inline">{locating ? t.detecting : t.detect}</span>
            </button>
          </div>
        </div>
        {/* Multi-restaurant / group */}
        <div className="rounded-2xl border border-neutral-200 bg-neutral-50 p-4" data-testid="onboarding-group-block">
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={form.is_group}
              onChange={(e) => setForm({ ...form, is_group: e.target.checked, locations: e.target.checked ? (form.locations.length ? form.locations : [{ name: '', city: '' }]) : [] })}
              className="mt-0.5 w-4 h-4 accent-[#F77F11]" data-testid="onboarding-group-toggle" />
            <span>
              <span className="flex items-center gap-1.5 text-[13.5px] font-bold text-neutral-900"><Building2 className="w-4 h-4 text-[#F77F11]" /> {t.group}</span>
              <span className="block mt-0.5 text-[12px] text-neutral-500 leading-relaxed">{t.groupSub}</span>
            </span>
          </label>
          {form.is_group && (
            <div className="mt-3 space-y-2">
              {form.locations.map((loc, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <input value={loc.name} placeholder={t.locName}
                    onChange={(e) => setForm({ ...form, locations: form.locations.map((l, j) => (j === i ? { ...l, name: e.target.value } : l)) })}
                    className="flex-1 min-w-0 h-10 rounded-xl border border-neutral-300 px-3 text-[13px] outline-none focus:border-[#F77F11]" data-testid={`onboarding-loc-name-${i}`} />
                  <input value={loc.city} placeholder={t.locCity}
                    onChange={(e) => setForm({ ...form, locations: form.locations.map((l, j) => (j === i ? { ...l, city: e.target.value } : l)) })}
                    className="w-32 sm:w-40 h-10 rounded-xl border border-neutral-300 px-3 text-[13px] outline-none focus:border-[#F77F11]" data-testid={`onboarding-loc-city-${i}`} />
                  <button type="button" onClick={() => setForm({ ...form, locations: form.locations.filter((_, j) => j !== i) })}
                    className="shrink-0 w-8 h-8 rounded-full border border-neutral-200 text-neutral-400 hover:text-red-500 hover:border-red-300 inline-flex items-center justify-center transition-colors" aria-label="Remove">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              {form.locations.length < 10 && (
                <button type="button" onClick={() => setForm({ ...form, locations: [...form.locations, { name: '', city: '' }] })}
                  className="inline-flex items-center gap-1.5 text-[12.5px] font-bold text-[#F77F11] hover:underline underline-offset-4" data-testid="onboarding-add-location">
                  <Plus className="w-3.5 h-3.5" /> {t.addLoc}
                </button>
              )}
            </div>
          )}
        </div>
        {err && <p className="text-[12.5px] text-red-500" data-testid="onboarding-error">{err}</p>}
        <button type="submit" disabled={busy}
          className="w-full h-12 rounded-xl bg-[#F77F11] hover:bg-[#d8651a] disabled:opacity-60 text-white font-bold text-[15px] transition-colors inline-flex items-center justify-center gap-2"
          data-testid="onboarding-save-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {busy ? t.saving : t.save}
        </button>
      </form>
    </div>
  );
};
